import { forwardRef, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Autocomplete, type AutocompleteProps } from 'ui';

export type AutocompleteOption = {
  value: string;
  label: string;
};
export type AutocompleteFieldProps<T, F extends boolean = false> = AutocompleteProps<T, F> & {
  label: string;
  name: string;
  valueField?: keyof T;
  onSelectOption?: (value: T) => void;
};

const Field = <T extends AutocompleteOption>(
  {
    name,
    options,
    required = false,
    valueField = 'value',
    onSelectOption,
    freeSolo,
    ...rest
  }: AutocompleteFieldProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>
) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      required: required ? 'This field is required' : undefined,
    },
  });
  const [value, setValue] = useState<T | undefined | null>();

  const { error } = fieldState;

  const inputValue = useMemo(() => {
    if (!field.value) {
      return undefined;
    }

    if (value) {
      return value;
    }

    const option = options.find((option) => option[valueField] === field.value);

    if (option) {
      return option;
    }

    return field.value;
  }, [field.value, options, value, valueField]);

  return (
    <Autocomplete<T>
      freeSolo={freeSolo}
      options={options}
      value={inputValue}
      required={required}
      onChange={(_event, newValue) => {
        if (!newValue) {
          setValue(null);
          return field.onChange(null);
        }
        setValue(newValue);
        const fieldValue = typeof newValue === 'string' ? newValue : newValue[valueField];
        field.onChange(fieldValue);
        onSelectOption?.(newValue);
      }}
      onInputChange={(_event, newInputValue) => {
        if (freeSolo) {
          field.onChange(newInputValue);
        }
      }}
      ref={ref}
      name={name}
      error={error ? error.message : undefined}
      {...rest}
    />
  );
};

export const AutocompleteField = forwardRef(Field) as <T, F extends boolean = false>(
  props: AutocompleteFieldProps<T, F>
) => React.ReactElement;
